import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

import { t } from "../i18n";
import { useAuth } from "../AuthContext";

/**
 * HourglassView — a near-full-screen elegant 3D hourglass that VISUALIZES an
 * external timer. It never owns the timer; each animation frame it reads
 * `elapsedRef.current` (seconds since Start) and derives:
 *   remainingSand = max(0, 1 - elapsed / ideal)
 *   elapsedSand   = 1 - remainingSand
 *
 * Drawn programmatically on <canvas> via requestAnimationFrame. No Lottie,
 * GIF, MP4 or pre-rendered animation. Targets 60 FPS with a single canvas
 * repaint per frame and a small pool of pre-allocated sand-stream particles.
 */
export default function HourglassView({
  open,
  onClose,
  elapsedRef,   // { current: number } — seconds since Start, updated by parent
  idealSec,     // number > 0          — configured Ideal Time in seconds
  running,      // boolean             — parent timer state
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeTextRef = useRef(null);

  /* --------------- pre-allocated particle pool for the stream --------- */
  const streamRef = useRef(null);
  if (streamRef.current == null) {
    streamRef.current = Array.from({ length: 34 }, (_, i) => ({
      phase: Math.random(),
      jx: (Math.random() - 0.5) * 1.6,
      speed: 0.9 + Math.random() * 0.3,
      r: 1.5 + Math.random() * 1.1,
      _seq: i,
    }));
  }

  useEffect(() => {
    if (!open) return undefined;
    // Lock underlying page scroll while the fullscreen hourglass is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const start = performance.now();

    /* -------- Path builders (reused each frame) --------------------- */
    const upperChamberPath = (xL, xR, cx, yTop, yN, neckHalf) => {
      // Blown-glass silhouette — quadratic curves swell out at the middle
      // then taper into the neck. Symmetric.
      const p = new Path2D();
      const midY = (yTop + yN) / 2;
      const outerBulgeX = Math.min((xR - xL) * 0.06, 14);
      p.moveTo(xL, yTop);
      // right side: from top-right down to neck
      p.lineTo(xR, yTop);
      p.bezierCurveTo(
        xR + outerBulgeX, midY - 6,
        cx + neckHalf + 2, yN - 22,
        cx + neckHalf, yN
      );
      // bottom edge across the neck
      p.lineTo(cx - neckHalf, yN);
      // left side: from neck back up
      p.bezierCurveTo(
        cx - neckHalf - 2, yN - 22,
        xL - outerBulgeX, midY - 6,
        xL, yTop
      );
      p.closePath();
      return p;
    };

    const lowerChamberPath = (xL, xR, cx, yBot, yN, neckHalf) => {
      const p = new Path2D();
      const midY = (yN + yBot) / 2;
      const outerBulgeX = Math.min((xR - xL) * 0.06, 14);
      p.moveTo(cx - neckHalf, yN);
      p.lineTo(cx + neckHalf, yN);
      p.bezierCurveTo(
        cx + neckHalf + 2, yN + 22,
        xR + outerBulgeX, midY + 6,
        xR, yBot
      );
      p.lineTo(xL, yBot);
      p.bezierCurveTo(
        xL - outerBulgeX, midY + 6,
        cx - neckHalf - 2, yN + 22,
        cx - neckHalf, yN
      );
      p.closePath();
      return p;
    };

    /* -------- 3D wooden cap (ellipse with front band) --------------- */
    const drawCap = (cx, cy, rx, ry, height) => {
      // Rear (back rim) — darker
      const backGrad = ctx.createLinearGradient(cx - rx, cy - ry, cx + rx, cy + ry);
      backGrad.addColorStop(0, "#3f2412");
      backGrad.addColorStop(1, "#5a3418");
      ctx.fillStyle = backGrad;
      ctx.beginPath();
      ctx.ellipse(cx, cy + height, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      // Vertical band (side of cap)
      const bandGrad = ctx.createLinearGradient(0, cy, 0, cy + height);
      bandGrad.addColorStop(0, "#8b4f22");
      bandGrad.addColorStop(0.4, "#a3612e");
      bandGrad.addColorStop(1, "#5a3418");
      ctx.fillStyle = bandGrad;
      ctx.beginPath();
      ctx.moveTo(cx - rx, cy);
      ctx.lineTo(cx - rx, cy + height);
      ctx.ellipse(cx, cy + height, rx, ry, 0, Math.PI, 0, true);
      ctx.lineTo(cx + rx, cy);
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI, false);
      ctx.closePath();
      ctx.fill();
      // Front (top) face — brighter with radial highlight
      const topGrad = ctx.createRadialGradient(cx - rx * 0.3, cy - ry * 0.4, 2, cx, cy, rx * 1.05);
      topGrad.addColorStop(0, "#c98849");
      topGrad.addColorStop(0.5, "#a3612e");
      topGrad.addColorStop(1, "#6b3f1e");
      ctx.fillStyle = topGrad;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      // Thin gold ring (highlight edge)
      ctx.strokeStyle = "rgba(255,222,155,0.55)";
      ctx.lineWidth = 1.2;
      ctx.stroke();
    };

    const drawPost = (x, yTop, yBot, w) => {
      // Cylindrical wooden post: horizontal gradient light→dark→light
      const g = ctx.createLinearGradient(x, 0, x + w, 0);
      g.addColorStop(0, "#5a3418");
      g.addColorStop(0.35, "#c9803f");
      g.addColorStop(0.6, "#a3612e");
      g.addColorStop(1, "#3f2412");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.roundRect(x, yTop, w, yBot - yTop, 3);
      ctx.fill();
      // Bright rim highlight
      ctx.strokeStyle = "rgba(255,220,170,0.35)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + w * 0.28, yTop + 3);
      ctx.lineTo(x + w * 0.28, yBot - 3);
      ctx.stroke();
    };

    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      const elapsedSec = elapsedRef?.current ?? 0;
      const ideal = Math.max(1, idealSec || 1);
      const remaining = Math.max(0, 1 - elapsedSec / ideal);
      const elapsed = 1 - remaining;
      const remainingSec = Math.max(0, ideal - elapsedSec);
      const finished = remaining <= 0;

      /* ---------- Geometry ---------------------------------------- */
      const cx = w / 2;
      const cy = h / 2;
      // Fit the hourglass to the fixed canvas (92vw × 72vh) — matches the
      // previous screen-fitness while retaining the new 3D drawing.
      const boxH = Math.min(h * 0.88, w * 1.55);
      const boxW = boxH * 0.52;
      const yTop = cy - boxH / 2;
      const yBot = cy + boxH / 2;
      const xL = cx - boxW / 2;
      const xR = cx + boxW / 2;
      const yN = cy;
      const neckHalf = boxW * 0.05;
      const capRx = boxW * 0.66;
      const capRy = boxH * 0.028;
      const capH = boxH * 0.025;

      ctx.clearRect(0, 0, w, h);

      /* ---------- Ground shadow (behind everything) -------------- */
      const shadowGrad = ctx.createRadialGradient(cx, yBot + capH * 3, 0, cx, yBot + capH * 3, boxW * 0.7);
      shadowGrad.addColorStop(0, "rgba(0,0,0,0.55)");
      shadowGrad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = shadowGrad;
      ctx.beginPath();
      ctx.ellipse(cx, yBot + capH * 3, boxW * 0.7, 14, 0, 0, Math.PI * 2);
      ctx.fill();

      /* ---------- Side wooden posts (behind glass) ---------------- */
      const postW = boxW * 0.055;
      drawPost(xL - postW - 2, yTop - capH * 0.4, yBot + capH * 0.4, postW);
      drawPost(xR + 2, yTop - capH * 0.4, yBot + capH * 0.4, postW);

      /* ---------- Bottom wooden cap (drawn before glass) --------- */
      drawCap(cx, yBot, capRx, capRy, capH);

      /* ---------- Chamber paths ----------------------------------- */
      const upper = upperChamberPath(xL, xR, cx, yTop, yN, neckHalf);
      const lower = lowerChamberPath(xL, xR, cx, yBot, yN, neckHalf);

      /* ---------- Glass body shadow behind chambers -------------- */
      const glassBackGrad = ctx.createLinearGradient(0, yTop, 0, yBot);
      glassBackGrad.addColorStop(0, "rgba(30,20,50,0.35)");
      glassBackGrad.addColorStop(0.5, "rgba(30,20,50,0.18)");
      glassBackGrad.addColorStop(1, "rgba(30,20,50,0.35)");
      ctx.fillStyle = glassBackGrad;
      ctx.fill(upper);
      ctx.fill(lower);

      /* ---------- Sand: UPPER chamber ---------------------------- */
      const surfUpperY = yN - remaining * (yN - yTop);
      const sandGrad = ctx.createLinearGradient(xL, yTop, xR, yBot);
      sandGrad.addColorStop(0, "#efb15a");
      sandGrad.addColorStop(0.3, "#f6c76b");
      sandGrad.addColorStop(0.55, "#e0a13f");
      sandGrad.addColorStop(1, "#8f611c");

      ctx.save();
      ctx.clip(upper);
      ctx.fillStyle = sandGrad;
      ctx.fillRect(xL - 20, surfUpperY, boxW + 40, yBot - surfUpperY);
      // Curved meniscus at surface — dips at centre where the stream is drawn.
      if (!finished && remaining > 0.01) {
        const dip = Math.min(14, remaining * 18);
        ctx.beginPath();
        ctx.fillStyle = "rgba(0,0,0,0.22)";
        ctx.moveTo(xL - 8, surfUpperY - 1);
        ctx.quadraticCurveTo(cx, surfUpperY + dip, xR + 8, surfUpperY - 1);
        ctx.lineTo(xR + 8, surfUpperY + 10);
        ctx.lineTo(xL - 8, surfUpperY + 10);
        ctx.closePath();
        ctx.fill();
      }
      // sand highlight sheen
      ctx.strokeStyle = "rgba(255,244,214,0.55)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(xL - 6, surfUpperY);
      ctx.quadraticCurveTo(cx, surfUpperY + 4, xR + 6, surfUpperY);
      ctx.stroke();
      ctx.restore();

      /* ---------- Sand: LOWER chamber (3D piled mound) ------------ */
      const surfLowerY = yBot - elapsed * (yBot - yN) * 0.92;
      ctx.save();
      ctx.clip(lower);
      // base fill
      ctx.fillStyle = sandGrad;
      ctx.fillRect(xL - 20, surfLowerY, boxW + 40, yBot - surfLowerY + 8);

      // 3D piled mound: an elliptical bump with radial gradient shading.
      if (elapsed > 0.01) {
        const moundH = Math.min(38, elapsed * 50);
        const moundW = Math.min(boxW * 0.75, elapsed * boxW * 1.4);
        const my = surfLowerY;
        const mx = cx;
        const mound = ctx.createRadialGradient(
          mx - moundW * 0.2, my - moundH * 0.6, 2,
          mx, my - moundH * 0.2, moundW * 0.75
        );
        mound.addColorStop(0, "#ffe08a");
        mound.addColorStop(0.55, "#e5aa48");
        mound.addColorStop(1, "#8f611c");
        ctx.fillStyle = mound;
        ctx.beginPath();
        ctx.moveTo(mx - moundW / 2, my);
        ctx.quadraticCurveTo(mx, my - moundH, mx + moundW / 2, my);
        ctx.closePath();
        ctx.fill();
        // Cast shadow on right
        ctx.fillStyle = "rgba(0,0,0,0.15)";
        ctx.beginPath();
        ctx.moveTo(mx, my - moundH * 0.55);
        ctx.quadraticCurveTo(mx + moundW * 0.35, my - moundH * 0.15, mx + moundW / 2, my);
        ctx.lineTo(mx, my);
        ctx.closePath();
        ctx.fill();
      }
      // sheen line
      ctx.strokeStyle = "rgba(255,244,214,0.45)";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(xL - 4, surfLowerY);
      ctx.quadraticCurveTo(cx, surfLowerY - 2, xR + 4, surfLowerY);
      ctx.stroke();
      ctx.restore();

      /* ---------- Sand stream ------------------------------------ */
      if (running && !finished) {
        const t = (performance.now() - start) / 1000;
        const streamTop = yN + 1;
        const streamBottom = Math.max(streamTop + 4, surfLowerY - 2);
        const streamLen = streamBottom - streamTop;
        const grains = streamRef.current;
        // outer glow
        ctx.save();
        ctx.strokeStyle = "rgba(255,220,140,0.35)";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(cx, streamTop);
        ctx.lineTo(cx, streamBottom);
        ctx.stroke();
        // grains
        ctx.fillStyle = "#ffd772";
        for (let i = 0; i < grains.length; i++) {
          const g = grains[i];
          const p = (t * g.speed + g.phase) % 1;
          const gy = streamTop + p * streamLen;
          const gx = cx + g.jx;
          ctx.beginPath();
          ctx.arc(gx, gy, g.r, 0, Math.PI * 2);
          ctx.fill();
        }
        // bright inner core
        ctx.strokeStyle = "rgba(255,240,190,0.9)";
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(cx, streamTop);
        ctx.lineTo(cx, streamBottom);
        ctx.stroke();
        ctx.restore();
      }

      /* ---------- 3D glass rim highlights + inner shadow --------- */
      // Bright specular highlight on left of each chamber
      ctx.save();
      ctx.clip(upper);
      const spec1 = ctx.createLinearGradient(xL, 0, xL + boxW * 0.5, 0);
      spec1.addColorStop(0, "rgba(255,255,255,0.35)");
      spec1.addColorStop(0.5, "rgba(255,255,255,0.08)");
      spec1.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = spec1;
      ctx.fillRect(xL - 4, yTop - 4, boxW * 0.5, yN - yTop + 8);
      // subtle right rim (darker) → depth
      const rim1 = ctx.createLinearGradient(xR - 12, 0, xR + 6, 0);
      rim1.addColorStop(0, "rgba(0,0,0,0)");
      rim1.addColorStop(1, "rgba(0,0,0,0.25)");
      ctx.fillStyle = rim1;
      ctx.fillRect(xR - 12, yTop - 4, 24, yN - yTop + 8);
      ctx.restore();

      ctx.save();
      ctx.clip(lower);
      const spec2 = ctx.createLinearGradient(xL, 0, xL + boxW * 0.5, 0);
      spec2.addColorStop(0, "rgba(255,255,255,0.30)");
      spec2.addColorStop(0.5, "rgba(255,255,255,0.05)");
      spec2.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = spec2;
      ctx.fillRect(xL - 4, yN - 4, boxW * 0.5, yBot - yN + 8);
      const rim2 = ctx.createLinearGradient(xR - 12, 0, xR + 6, 0);
      rim2.addColorStop(0, "rgba(0,0,0,0)");
      rim2.addColorStop(1, "rgba(0,0,0,0.25)");
      ctx.fillStyle = rim2;
      ctx.fillRect(xR - 12, yN - 4, 24, yBot - yN + 8);
      ctx.restore();

      // Glass outer edge stroke (double: dark inner + light outer)
      ctx.strokeStyle = "rgba(0,0,0,0.35)";
      ctx.lineWidth = 2.4;
      ctx.stroke(upper);
      ctx.stroke(lower);
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      ctx.lineWidth = 1.2;
      ctx.stroke(upper);
      ctx.stroke(lower);

      /* ---------- Metallic neck ring (3D) ------------------------ */
      const ringH = Math.max(4, boxH * 0.014);
      const ringW = neckHalf * 3.4;
      const ringGrad = ctx.createLinearGradient(0, yN - ringH, 0, yN + ringH);
      ringGrad.addColorStop(0, "#8a5a1e");
      ringGrad.addColorStop(0.5, "#f0c46a");
      ringGrad.addColorStop(1, "#6b3f1e");
      ctx.fillStyle = ringGrad;
      ctx.beginPath();
      ctx.roundRect(cx - ringW / 2, yN - ringH, ringW, ringH * 2, ringH);
      ctx.fill();
      // highlight
      ctx.strokeStyle = "rgba(255,240,190,0.75)";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(cx - ringW / 2 + 4, yN - ringH * 0.35);
      ctx.lineTo(cx + ringW / 2 - 4, yN - ringH * 0.35);
      ctx.stroke();

      /* ---------- Top wooden cap (drawn last for depth) --------- */
      drawCap(cx, yTop, capRx, capRy, capH);

      /* ---------- Time text (DOM, not canvas) ------------------- */
      if (timeTextRef.current) {
        const s = Math.max(0, Math.floor(remainingSec));
        const mm = String(Math.floor(s / 60)).padStart(2, "0");
        const ss = String(s % 60).padStart(2, "0");
        timeTextRef.current.textContent = `${mm}:${ss}`;
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [open, elapsedRef, idealSec, running]);

  const { lang } = useAuth();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="hourglass-view"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] overflow-hidden flex flex-col items-center justify-center
            bg-gradient-to-b from-[#1a0f22] via-[#0f1b2c] to-[#080d18] backdrop-blur-md"
        >
          {/* soft radial vignette for depth */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 45% at 50% 55%, rgba(255,220,140,0.10), transparent 70%)",
            }}
          />
          <button
            data-testid="hourglass-exit-btn"
            onClick={onClose}
            className="absolute top-5 right-5 z-10 inline-flex items-center gap-2 bg-white/90 hover:bg-white text-slate-800 font-heading font-bold uppercase text-sm px-4 py-2.5 rounded-full border-2 border-white shadow-[0_4px_0_rgba(0,0,0,0.18)] active:translate-y-0.5 transition-all"
          >
            <X size={16} strokeWidth={3} /> {t(lang, "hourglass_exit")}
          </button>

          <motion.canvas
            ref={canvasRef}
            initial={{ scale: 0.94 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 20 }}
            data-testid="hourglass-canvas"
            className="w-[92vw] max-w-[440px] h-[72vh] max-h-[720px] block"
          />

          <div
            data-testid="hourglass-time"
            ref={timeTextRef}
            className="mt-4 font-heading text-5xl sm:text-6xl font-bold text-amber-100 tabular-nums drop-shadow-[0_4px_18px_rgba(245,197,95,0.35)]"
          >
            00:00
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
